import {
  AddIcon,
  Alert,
  Button,
  Divider,
  Dropdown,
  Flex,
  Menu,
  QnaIcon,
  Text,
} from "@fluentui/react-northstar";
import { TeamsSurveyQnaResponse, TeamsSurveyQnaResponseState } from "@teams/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";

import { useSurvey } from "../../../../../_shared/actions/useSurvey";
import { TeamsLoader } from "../../../../../_shared/components/TeamsLoader";
import { isError } from "../../../../../_shared/services/ChannelApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { useInterval } from "../../../../../_shared/utils/useInterval";
import { QnaQuestionForm } from "./QnaQuestionForm";
import { QnaResultsCard } from "./QnaResultsCard";

type QnaResultsDetailsProps = {
  surveyId: string;
};

type SortMethod = "Newest" | "Likes";

export const QnaResultsDetails: React.FC<QnaResultsDetailsProps> = ({ surveyId }) => {
  const { teamsContext } = useTeamsContext();

  const { survey, isSurveyLoading, loadSurvey, surveyResponses, surveyError } =
    useSurvey<TeamsSurveyQnaResponse>({ loadResponses: true });
  const [selectedTab, setSelectedTab] = useState<TeamsSurveyQnaResponseState>("approved");
  const [sortMethod, setSortMethod] = useState<SortMethod>("Newest");
  const [isAskingQuestion, setIsAskingQuestion] = useState<boolean>();

  useEffect(() => {
    (async () => {
      const result = await loadSurvey(teamsContext, surveyId);
      if (!isError(result)) {
        if (result.survey.role === "own" && result.survey.requireApproval) {
          setSelectedTab("pending");
        }
      }
    })();
  }, [surveyId, loadSurvey, teamsContext]);

  useInterval(
    () => {
      if (survey?.state !== "closed") {
        loadSurvey(teamsContext, surveyId, { hideLoader: true });
      }
    },
    survey?.role === "own" ? 2000 : 5000
  );

  const sort = useCallback(
    (a: TeamsSurveyQnaResponse, b: TeamsSurveyQnaResponse) => {
      switch (sortMethod) {
        case "Likes":
          return (
            b.reactions.filter(r => r.reaction === ":thumbsup:").length -
            a.reactions.filter(r => r.reaction === ":thumbsup:").length
          );
        case "Newest":
          return b.stateChangedAt.getTime() - a.stateChangedAt.getTime();
      }
    },
    [sortMethod]
  );

  const approvedResponses = useMemo(() => {
    const approved = surveyResponses.filter(r => r.state === "approved" && !r.focused).sort(sort);
    const focused = surveyResponses.filter(r => r.focused);
    if (focused.length) {
      return [...focused, ...approved];
    }

    return approved;
  }, [surveyResponses, sort]);

  const doneResponses = useMemo(() => {
    return surveyResponses
      .filter(r => r.state === "done")
      .sort((a, b) => b.stateChangedAt.getTime() - a.stateChangedAt.getTime());
  }, [surveyResponses]);

  const pendingResponses = useMemo(() => {
    return surveyResponses.filter(r => r.state === "pending").sort(sort);
  }, [surveyResponses, sort]);

  const dismissedResponses = useMemo(() => {
    return surveyResponses.filter(r => r.state === "dismissed").sort(sort);
  }, [surveyResponses, sort]);

  const [viewResponses, flipKey] = useMemo(() => {
    let items: TeamsSurveyQnaResponse[];
    switch (selectedTab) {
      case "pending":
        items = pendingResponses;
        break;
      case "dismissed":
        items = dismissedResponses;
        break;
      case "approved":
      default:
        items = approvedResponses;
        break;
    }

    const fk = items.reduce((a, r) => `${a}|${r.id}:${r.focused === true}`, "");

    return [items, fk];
  }, [selectedTab, pendingResponses, dismissedResponses, approvedResponses]);

  const items = useMemo(
    () => [
      {
        key: "approved",
        content: `Approved (${approvedResponses.length})`,
        onClick: () => setSelectedTab("approved"),
      },
      {
        key: "pending",
        content: `Review (${pendingResponses.length})`,
        onClick: () => setSelectedTab("pending"),
      },
      {
        key: "dismissed",
        content: `Dismissed (${dismissedResponses.length})`,
        onClick: () => setSelectedTab("dismissed"),
      },
    ],
    [approvedResponses.length, pendingResponses.length, dismissedResponses.length]
  );

  return (
    <TeamsLoader isLoading={isSurveyLoading}>
      {surveyError && <Alert danger content={surveyError.message} />}

      {survey && (
        <Flex column gap="gap.small">
          <Flex column gap="gap.medium">
            {!isAskingQuestion && survey.state === "open" && (
              <>
                <Text size="large" content={survey.title} />

                <Button
                  fluid
                  primary
                  icon={<AddIcon />}
                  content="Submit a question"
                  onClick={() => setIsAskingQuestion(true)}
                />
              </>
            )}

            {isAskingQuestion && (
              <div
                style={{
                  background: "var(--stardustui-colorScheme-default-background)",
                  padding: "4px",
                }}
              >
                <QnaQuestionForm
                  survey={survey}
                  trackingSource="details"
                  onCompleted={() => {
                    setIsAskingQuestion(false);
                    loadSurvey(teamsContext, surveyId, { hideLoader: true });
                  }}
                />
              </div>
            )}
          </Flex>

          {survey.role === "own" && (
            <Menu
              fluid
              underlined
              primary
              style={{ borderBottom: "unset", flexWrap: "wrap", fontSize: "0.9em" }}
              items={items}
              activeIndex={items.findIndex(item => item.key === selectedTab)}
            />
          )}

          {selectedTab === "approved" && viewResponses.length > 0 && (
            <Flex column>
              <Flex.Item align="end">
                <Dropdown
                  fluid
                  items={["Newest", "Likes"] as SortMethod[]}
                  value={sortMethod}
                  placeholder={sortMethod}
                  onChange={(_, b) => setSortMethod(b.value as SortMethod)}
                />
              </Flex.Item>
              <Divider fitted />
            </Flex>
          )}

          <Flex column gap="gap.large">
            <Flipper flipKey={flipKey} spring="veryGentle">
              <div style={{ minHeight: "40vh" }}>
                {viewResponses.length > 0 && (
                  <Flex column gap="gap.small">
                    {viewResponses.map(response => (
                      <Flipped flipId={response.id} key={response.id}>
                        {flippedProps => (
                          <QnaResultsCard
                            key={response.id}
                            survey={survey}
                            response={response}
                            onChanged={() =>
                              loadSurvey(teamsContext, surveyId, { hideLoader: true })
                            }
                            flippedProps={flippedProps}
                          />
                        )}
                      </Flipped>
                    ))}
                  </Flex>
                )}
                {viewResponses.length === 0 && (
                  <Flex column hAlign="center" gap="gap.medium">
                    <Text as="div" content="No questions" align="center" size="large" />
                    <QnaIcon size="largest" />
                  </Flex>
                )}
              </div>

              {selectedTab === "approved" && (
                <>
                  <Divider content={`Answered (${doneResponses.length})`} size={2} color="brand" />

                  <Flex column gap="gap.small">
                    {doneResponses.map(response => (
                      <Flipped flipId={response.id} key={response.id}>
                        {flippedProps => (
                          <QnaResultsCard
                            key={response.id}
                            survey={survey}
                            response={response}
                            onChanged={() =>
                              loadSurvey(teamsContext, surveyId, { hideLoader: true })
                            }
                            flippedProps={flippedProps}
                          />
                        )}
                      </Flipped>
                    ))}
                  </Flex>
                </>
              )}
            </Flipper>
          </Flex>
        </Flex>
      )}
    </TeamsLoader>
  );
};
